import { useMemo } from 'react';
import { useAuth } from './useAuth';
import { CognitoUser } from '../types/auth';
import { impersonationService } from '../services/impersonationService';

interface UserRole {
  type: 'cognito_super_admin' | 'cognito_admin' | 'entity_admin' | 'entity_event_admin';
  entityId?: string;
  entityName?: string;
}

export const useAdminAccess = () => {
  const { user, profile } = useAuth();

  const getCognitoRole = (user: CognitoUser): string | null => {
    // Check all possible locations for Cognito custom roles
    const possibleRoles = [user?.['custom:roles'], user?.['custom:custom:roles']];

    // Find the first non-null role value
    for (const role of possibleRoles) {
      if (role && typeof role === 'string') {
        return role;
      }
    }

    return null;
  };

  const userRoles = useMemo((): UserRole[] => {
    if (!user) {
      return [];
    }

    const roles: UserRole[] = [];

    // While a super-admin is "viewing as" another member, ignore their own Cognito
    // platform role so the UI renders exactly as the target member sees it. The
    // target's entity-admin status still comes through via profile.entityMemberships.
    const cognitoRole = impersonationService.isActive() ? null : getCognitoRole(user);

    if (cognitoRole === 'super_admin') {
      roles.push({ type: 'cognito_super_admin' });
    } else if (cognitoRole === 'admin') {
      roles.push({ type: 'cognito_admin' });
    }

    // Check entity-level roles from profile
    if (profile?.entityMemberships) {
      for (const membership of profile.entityMemberships) {
        if (membership.status !== 'active') continue;

        const memberRole = membership.role || '';
        const hasAdminRole = ['Admin', 'admin', 'President', 'Vice President'].includes(memberRole);
        const hasEventAdminRole = ['Event Admin', 'Event Coordinator'].includes(memberRole);

        if (hasAdminRole) {
          roles.push({
            type: 'entity_admin',
            entityId: membership.entityId,
          });
        } else if (hasEventAdminRole) {
          roles.push({
            type: 'entity_event_admin',
            entityId: membership.entityId,
          });
        }
      }
    }

    return roles;
  }, [user, profile]);

  const hasRole = (roleType: UserRole['type']): boolean => {
    return userRoles.some(role => role.type === roleType);
  };

  const canManageAllMembers = (): boolean => {
    return hasRole('cognito_super_admin') || hasRole('cognito_admin');
  };

  const canManageEntityMembers = (): boolean => {
    return userRoles.some(
      role =>
        role.type === 'entity_admin' ||
        role.type === 'entity_event_admin' ||
        role.type === 'cognito_super_admin' ||
        role.type === 'cognito_admin',
    );
  };

  const hasAnyAdminRole = (): boolean => {
    return userRoles.length > 0;
  };

  const isSuperAdmin = (): boolean => {
    return hasRole('cognito_super_admin');
  };

  const isCognitoAdmin = (): boolean => {
    return hasRole('cognito_super_admin') || hasRole('cognito_admin');
  };

  const getAdminEntityIds = (): string[] => {
    return userRoles
      .filter(role => (role.type === 'entity_admin' || role.type === 'entity_event_admin') && role.entityId)
      .map(role => role.entityId!);
  };

  return {
    userRoles,
    hasRole,
    canManageAllMembers,
    canManageEntityMembers,
    hasAnyAdminRole,
    isSuperAdmin,
    isCognitoAdmin,
    getAdminEntityIds,
  };
};
